import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useCollection, useFirestoreMutations } from '../../hooks/useFirestore'
import { backfillAllReciprocals } from '../../lib/relationshipSync'
import { getActiveImage } from '../../lib/characterUtils'
import CharacterImport from './CharacterImport'
import './AdminEditor.css'

const PLACEHOLDER_IMAGE = 'data:image/svg+xml,' + encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
    <rect fill="#1E1A22" width="100" height="100"/>
    <circle cx="50" cy="35" r="20" fill="#6E2EBF"/>
    <ellipse cx="50" cy="80" rx="30" ry="25" fill="#6E2EBF"/>
  </svg>
`)

function SortableCharacterItem({ character, badge, badgeClass }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: character.id })

  const isInactive = character.active === false
  const isHidden = character.hidden === true

  // Get active image (true form if revealed, otherwise profile)
  const activeImage = getActiveImage(character)

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : (isHidden ? 0.5 : (isInactive ? 0.6 : 1)),
    zIndex: isDragging ? 1000 : 'auto'
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`admin-list-item sortable-item ${isDragging ? 'dragging' : ''} ${isInactive ? 'inactive' : ''} ${isHidden ? 'hidden-item' : ''}`}
    >
      <div className="drag-handle" {...attributes} {...listeners}>
        <span className="drag-icon">⋮⋮</span>
      </div>
      <Link
        to={`/admin/characters/${character.id}`}
        className="item-content"
      >
        <div className="item-image">
          <img
            src={activeImage.url || PLACEHOLDER_IMAGE}
            alt={character.name}
            onError={(e) => { e.target.src = PLACEHOLDER_IMAGE }}
          />
        </div>
        <div className="item-info">
          <h3>
            {character.name}
            {isHidden && <span className="hidden-tag">Hidden</span>}
            {isInactive && !isHidden && <span className="inactive-tag">Inactive</span>}
          </h3>
          <p className="text-secondary">
            {[character.race, character.class].filter(Boolean).join(' • ') || 'No details'}
          </p>
        </div>
        <span className={`item-badge ${badgeClass}`}>{badge}</span>
      </Link>
    </div>
  )
}

export default function CharacterList() {
  const { data: characters, loading } = useCollection('characters', { orderByField: 'sortOrder' })
  const { update } = useFirestoreMutations('characters')
  const [showImport, setShowImport] = useState(false)
  const [saving, setSaving] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [syncResult, setSyncResult] = useState(null)

  const handleSyncReciprocals = async () => {
    if (!window.confirm('This will create missing reciprocal relationships for all characters. Continue?')) {
      return
    }

    setSyncing(true)
    setSyncResult(null)

    try {
      const stats = await backfillAllReciprocals()
      setSyncResult({
        success: stats.errors.length === 0,
        message: `Processed ${stats.charactersProcessed} characters. Created ${stats.reciprocalsCreated} reciprocals, updated ${stats.reciprocalsUpdated}.${stats.errors.length > 0 ? ` Errors: ${stats.errors.length}` : ''}`
      })
    } catch (err) {
      setSyncResult({
        success: false,
        message: `Sync failed: ${err.message}`
      })
    }

    setSyncing(false)
  }

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  const pcs = characters.filter(c => c.type === 'pc')
  const npcs = characters.filter(c => c.type === 'npc')

  const handleDragEnd = async (event, characterList, type) => {
    const { active, over } = event

    if (!over || active.id === over.id) return

    const oldIndex = characterList.findIndex(c => c.id === active.id)
    const newIndex = characterList.findIndex(c => c.id === over.id)

    if (oldIndex === -1 || newIndex === -1) return

    // Reorder the list
    const reordered = arrayMove(characterList, oldIndex, newIndex)

    // Calculate new sort orders
    // We'll use the position in the combined list, offset by type
    // PCs get lower numbers (0-999), NPCs get higher (1000+)
    const baseOffset = type === 'pc' ? 0 : 1000

    setSaving(true)

    try {
      // Update each character's sortOrder
      const updates = reordered.map((char, index) => ({
        id: char.id,
        sortOrder: baseOffset + index
      }))

      await Promise.all(
        updates.map(({ id, sortOrder }) => update(id, { sortOrder }))
      )
    } catch (err) {
      console.error('Failed to update sort order:', err)
    }

    setSaving(false)
  }

  if (loading) {
    return (
      <div className="container">
        <div className="loading-spinner" />
      </div>
    )
  }

  return (
    <div className="container">
      <div className="editor-header">
        <h1>Characters</h1>
        <div className="header-actions">
          {saving && <span className="saving-indicator">Saving...</span>}
          <button
            type="button"
            className="btn btn-ghost"
            onClick={handleSyncReciprocals}
            disabled={syncing}
          >
            {syncing ? 'Syncing...' : 'Sync Reciprocals'}
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setShowImport(true)}
          >
            Import CSV
          </button>
          <Link to="/admin/characters/new" className="btn btn-primary">
            + New Character
          </Link>
        </div>
      </div>

      {syncResult && (
        <div className={`sync-result ${syncResult.success ? 'success' : 'error'}`}>
          {syncResult.message}
          <button
            type="button"
            className="dismiss-btn"
            onClick={() => setSyncResult(null)}
          >
            ×
          </button>
        </div>
      )}

      {characters.length === 0 ? (
        <div className="empty-state">
          <p>No characters yet.</p>
          <p className="text-secondary">Create your first character to get started.</p>
        </div>
      ) : (
        <>
          {pcs.length > 0 && (
            <section className="admin-section">
              <h2>Player Characters ({pcs.length})</h2>
              <p className="help-text">Drag to reorder</p>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={(e) => handleDragEnd(e, pcs, 'pc')}
              >
                <SortableContext
                  items={pcs.map(c => c.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="admin-list sortable-list">
                    {pcs.map(character => (
                      <SortableCharacterItem
                        key={character.id}
                        character={character}
                        badge="PC"
                        badgeClass="badge-pc"
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </section>
          )}

          {npcs.length > 0 && (
            <section className="admin-section">
              <h2>Non-Player Characters ({npcs.length})</h2>
              <p className="help-text">Drag to reorder</p>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={(e) => handleDragEnd(e, npcs, 'npc')}
              >
                <SortableContext
                  items={npcs.map(c => c.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="admin-list sortable-list">
                    {npcs.map(character => (
                      <SortableCharacterItem
                        key={character.id}
                        character={character}
                        badge="NPC"
                        badgeClass="badge-npc"
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </section>
          )}
        </>
      )}

      <CharacterImport
        isOpen={showImport}
        onClose={() => setShowImport(false)}
      />
    </div>
  )
}
